import { useId, useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import connectionsCopy from '@/i18n/ns/connections';
import { WorkspaceIcon } from '../icons';
import { CopyLine } from './copy-line';
import { Glyph } from './glyphs';
import { SetupPrompt } from './setup-prompt';
import type { AgentAddress } from './types';

const json = (value: unknown) => JSON.stringify(value, null, 2);

/**
 * The same three steps for every external agent: Poneva is one standard
 * remote MCP server, so nothing here depends on which agent the person uses.
 * The address itself is the one field above the steps.
 */
export function AppSetup({
  address,
  onCreateKey,
}: {
  address: AgentAddress;
  /** Opens the access-key form; absent while it is already open or not offered. */
  onCreateKey?: () => void;
}) {
  const copy = useNamespaceCopy(connectionsCopy);
  const heading = useId();
  const snippet = useId();
  const [showSnippet, setShowSnippet] = useState(false);
  return (
    <div className="ws-agent-setup" role="group" aria-labelledby={heading}>
      <h3 id={heading}>{copy.setupTitle}</h3>
      <SetupPrompt address={address} />
      <p className="ws-agent-manual-label">{copy.setupManualLabel}</p>
      <ol className="ws-agent-steps">
        <li>
          <h4>{copy.setupAddTitle}</h4>
          <p>{copy.setupAddBody}</p>
          <button
            type="button"
            className="ws-agent-disclosure"
            aria-expanded={showSnippet}
            aria-controls={showSnippet ? snippet : undefined}
            onClick={() => setShowSnippet((value) => !value)}
          >
            <WorkspaceIcon name="down" size={14} />
            {copy.setupConfigToggle}
          </button>
          {showSnippet && (
            <div id={snippet} className="ws-agent-snippet">
              <p>{copy.setupConfigBody}</p>
              <CopyLine
                value={json({ mcpServers: { poneva: { type: 'http', url: address.url } } })}
                label={copy.copyWhatConfig}
                kind="file"
                caption="JSON · mcpServers"
              />
            </div>
          )}
        </li>
        <li>
          <h4>{copy.setupSignInTitle}</h4>
          <p>{copy.setupSignInBody}</p>
          <p>{copy.setupSignInFallback}</p>
          {onCreateKey && (
            <button type="button" className="ws-agent-step-action" onClick={onCreateKey}>
              <Glyph name="key" size={14} />
              {copy.keyCreateAction}
            </button>
          )}
        </li>
        <li>
          <h4>{copy.setupTryTitle}</h4>
          <p>{copy.setupTryAsk}</p>
          <CopyLine value={copy.setupSampleQuestion} label={copy.copyWhatSample} kind="text" />
          <p className="ws-agent-step-note">{copy.setupTryNote}</p>
        </li>
      </ol>
    </div>
  );
}
